import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { CardShell, FieldLabel } from "./card-handler";

// Same base URL convention as bot-form-req.tsx — overridable via env.
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "https://journeybuilder.connect.dalmlabs.com";

// Signals the backend's wait_till step listens for. Must match the names the
// backend's `_handle_wait_till` understands (`"email" in event_name` etc).
const EVENT_OPTIONS = [
  {
    label: "Email Engagement (open/click/bounce)",
    value: "email_engagement_received",
  },
  {
    label: "Call Outcome (answered/no-answer)",
    value: "call_outcome_received",
  },
  {
    label: "Inbound Reply (WhatsApp)",
    value: "handle_external_event",
  },
];

// Fallback list — fires only when GET /v1/variables/dates fails. Matches the
// canonical date attributes seeded by the backend for Appointment_Scheduled.
const FALLBACK_DATE_ATTRIBUTES: Array<{ name: string; label: string }> = [
  { name: "appointment_date",        label: "Appointment Date" },
  { name: "scheduled_date_and_time", label: "Scheduled Date And Time" },
];

interface BackendVariable {
  name: string;
  label: string;
  type: string;
  source: string;
  is_date?: boolean;
}

interface WaitTillConfig {
  days?: number;
  hours?: number;
  minutes?: number;
  waitForEvent?: string;
  timeoutContinue?: boolean;
  waitMode?: "signal" | "date";
  dateAttribute?: string;
  offsetValue?: number;
  offsetUnit?: string;
  offsetDirection?: string;
}

interface WaitTillCardProps {
  config: WaitTillConfig;
  onUpdate: (config: WaitTillConfig) => void;
  onDelete: () => void;
  onReset: () => void;
  // Optional — when the parent passes the chosen trigger's event_type
  // (e.g. "Appointment_Scheduled"), the card narrows the date-attribute
  // dropdown to fields that event actually exposes. Without it we fall
  // back to the static list above.
  eventType?: string;
  bare?: boolean;
}

export const WaitTillCard: React.FC<WaitTillCardProps> = ({
  config,
  onUpdate,
  onDelete,
  onReset,
  eventType,
  bare,
}) => {
  const [eventOpen, setEventOpen] = useState(false);
  const eventRef = useRef<HTMLDivElement>(null);
  const [dateAttrs, setDateAttrs] =
    useState<Array<{ name: string; label: string }>>(FALLBACK_DATE_ATTRIBUTES);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (eventRef.current && !eventRef.current.contains(e.target as Node)) {
        setEventOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch the date attributes available for the chosen trigger's event_type.
  // Backend: GET /v1/variables/dates?event_type=<name> returns lead-data date
  // fields PLUS the event-specific date fields from EventTypeDefinition.
  // Re-fires when eventType changes so picking a different trigger updates
  // the dropdown immediately.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("authToken");
        const url = eventType
          ? `${BACKEND_URL}/v1/variables/dates?event_type=${encodeURIComponent(eventType)}`
          : `${BACKEND_URL}/v1/variables/dates`;
        const res = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: BackendVariable[] = await res.json();
        if (cancelled) return;
        const next = (data ?? [])
          .filter((v) => v.is_date)
          .map((v) => ({ name: v.name, label: v.label || v.name }));
        if (next.length > 0) setDateAttrs(next);
      } catch (err) {
        console.warn("[WaitTill] Could not fetch /v1/variables/dates — using fallback list", err);
      }
    })();
    return () => { cancelled = true; };
  }, [eventType]);

  const update = (patch: Partial<WaitTillConfig>) => onUpdate({ ...config, ...patch });

  const display = {
    days: config.days ?? 2,
    hours: config.hours ?? 0,
    minutes: config.minutes ?? 0,
    waitForEvent: config.waitForEvent ?? "email_engagement_received",
    timeoutContinue: config.timeoutContinue ?? true,
    waitMode: config.waitMode ?? "signal",
    dateAttribute: config.dateAttribute ?? "Appointment_Date",
    offsetValue: config.offsetValue ?? 1,
    offsetUnit: config.offsetUnit ?? "Days",
    offsetDirection: config.offsetDirection ?? "Before",
  };

  const selectedEvent = EVENT_OPTIONS.find((e) => e.value === display.waitForEvent);
  const isDateWait = display.waitMode === "date";

  return (
    <CardShell
      title={isDateWait ? "Wait Until" : "Wait Till"}
      description={
        isDateWait
          ? "Wait until a specific date and time condition is met"
          : "Pause until the signal arrives, with a maximum wait."
      }
      onDelete={onDelete}
      onReset={onReset}
      bare={bare}
    >
        {isDateWait ? (
          <>
            <FieldLabel>Date Attribute</FieldLabel>
            <div className="relative mb-4">
              <select
                value={display.dateAttribute}
                onChange={(e) => update({ dateAttribute: e.target.value })}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              >
                {dateAttrs.map((a) => (
                  <option key={a.name} value={a.name}>{a.label}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-2 top-2.5 text-gray-500 dark:text-gray-400 pointer-events-none" />
            </div>

            <FieldLabel>Trigger</FieldLabel>
            <div className="grid grid-cols-[0.8fr_1fr_1fr] gap-2 mb-2">
              <input
                type="number"
                min={0}
                value={display.offsetValue}
                onChange={(e) => {
                  const num = parseInt(e.target.value, 10);
                  update({ offsetValue: isNaN(num) ? 0 : num });
                }}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-center outline-none focus:ring-1 focus:ring-indigo-300"
              />
              <div className="relative">
                <select
                  value={display.offsetUnit}
                  onChange={(e) => update({ offsetUnit: e.target.value })}
                  className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                >
                  <option>Minutes</option>
                  <option>Hours</option>
                  <option>Days</option>
                  <option>Weeks</option>
                </select>
                <ChevronDown size={16} className="absolute right-2 top-2.5 text-gray-500 dark:text-gray-400 pointer-events-none" />
              </div>
              <div className="relative">
                <select
                  value={display.offsetDirection}
                  onChange={(e) => update({ offsetDirection: e.target.value })}
                  className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                >
                  <option>Before</option>
                  <option>After</option>
                  <option>At</option>
                </select>
                <ChevronDown size={16} className="absolute right-2 top-2.5 text-gray-500 dark:text-gray-400 pointer-events-none" />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Trigger {display.offsetDirection.toLowerCase()} scheduled time
            </p>
          </>
        ) : (
          <>
            {/* Event picker */}
            <FieldLabel>Wait For</FieldLabel>
            <div className="relative mb-3" ref={eventRef}>
              <button
                type="button"
                onClick={() => setEventOpen(!eventOpen)}
                className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-left flex items-center justify-between bg-white dark:bg-gray-900 focus:outline-none focus:ring-1 focus:ring-indigo-300"
              >
                <span className="text-gray-700 dark:text-gray-300">{selectedEvent?.label || "Select signal"}</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${eventOpen ? "rotate-180" : ""}`}
                />
              </button>
              {eventOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                  {EVENT_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        update({ waitForEvent: opt.value });
                        setEventOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 ${
                        display.waitForEvent === opt.value
                          ? "bg-indigo-50 text-indigo-600"
                          : ""
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Max wait */}
            <FieldLabel>Maximum Wait</FieldLabel>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {(["days", "hours", "minutes"] as const).map((unit) => (
                <div key={unit} className="flex flex-col items-center">
                  <input
                    type="number"
                    min={0}
                    value={display[unit]}
                    onChange={(e) => {
                      const num = parseInt(e.target.value, 10);
                      update({ [unit]: isNaN(num) ? 0 : num } as Partial<WaitTillConfig>);
                    }}
                    className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-center outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                  <span className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 capitalize">{unit}</span>
                </div>
              ))}
            </div>

            <label className="flex items-center gap-2 mt-3 cursor-pointer">
              <input
                type="checkbox"
                checked={display.timeoutContinue}
                onChange={(e) => update({ timeoutContinue: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              <span className="text-xs text-gray-600 dark:text-gray-300">
                Continue to next step if signal never arrives
              </span>
            </label>
          </>
        )}
    </CardShell>
  );
};